export interface RawCSVRow {
  contact_name: string
  contact_phone: string
  dropoff_address: string
  size: string
  weight: string
  fragile: string
  payment_method: string
  price: string
  notes: string
}

export interface ParsedRow {
  contactName: string
  contactPhone: string
  dropoffAddress: string
  size: 'small' | 'medium' | 'large' | 'extra'
  weight?: number
  fragile: boolean
  paymentMethod: 'cash' | 'online'
  price: number
  notes?: string
}

export interface RowResult {
  index: number
  raw: RawCSVRow
  parsed?: ParsedRow
  errors: string[]
  valid: boolean
}

export interface CSVParseResult {
  rows: RowResult[]
  validCount: number
  invalidCount: number
  totalCount: number
}

const splitCSVLine = (line: string): string[] => {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === ',' && !inQuotes) {
      result.push(current)
      current = ''
    } else {
      current += char
    }
  }
  result.push(current)
  return result
}

const validateRow = (raw: RawCSVRow, index: number): RowResult => {
  const errors: string[] = []

  if (!raw.contact_name || raw.contact_name.length < 2)
    errors.push('Nom requis (min 2 caractères)')

  if (!/^\d{8}$/.test(raw.contact_phone))
    errors.push('Téléphone invalide (8 chiffres sans +216)')

  if (!raw.dropoff_address || raw.dropoff_address.length < 5)
    errors.push('Adresse requise (min 5 caractères)')

  const validSizes = ['small', 'medium', 'large', 'extra']
  const size = raw.size || 'medium'
  if (!validSizes.includes(size))
    errors.push(`Taille invalide : "${raw.size}" (valeurs : small, medium, large, extra)`)

  let weight: number | undefined
  if (raw.weight) {
    weight = parseFloat(raw.weight)
    if (isNaN(weight) || weight <= 0)
      errors.push('Poids invalide (nombre positif)')
  }

  const fragileStr = raw.fragile.toLowerCase()
  if (raw.fragile && !['true', 'false', '1', '0', ''].includes(fragileStr))
    errors.push('Fragile invalide (true ou false)')

  if (!['cash', 'online'].includes(raw.payment_method))
    errors.push(`Mode de paiement invalide : "${raw.payment_method}" (cash ou online)`)

  const price = parseFloat(raw.price)
  if (isNaN(price) || price <= 0)
    errors.push('Prix invalide (nombre positif obligatoire)')

  if (errors.length > 0) {
    return { index, raw, errors, valid: false }
  }

  const parsed: ParsedRow = {
    contactName:   raw.contact_name,
    contactPhone:  `+216${raw.contact_phone}`,
    dropoffAddress: raw.dropoff_address,
    size:          size as ParsedRow['size'],
    weight,
    fragile:       ['true', '1'].includes(raw.fragile.toLowerCase()),
    paymentMethod: raw.payment_method as 'cash' | 'online',
    price,
    notes:         raw.notes || undefined,
  }

  return { index, raw, parsed, errors: [], valid: true }
}

export const parseCSVContent = (content: string): CSVParseResult => {
  const lines = content
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)

  const dataLines = lines.slice(1)

  const rows: RowResult[] = dataLines.map((line, idx) => {
    const cols = splitCSVLine(line)
    const raw: RawCSVRow = {
      contact_name:    (cols[0] ?? '').trim(),
      contact_phone:   (cols[1] ?? '').trim(),
      dropoff_address: (cols[2] ?? '').trim(),
      size:            (cols[3] ?? '').trim(),
      weight:          (cols[4] ?? '').trim(),
      fragile:         (cols[5] ?? '').trim(),
      payment_method:  (cols[6] ?? '').trim(),
      price:           (cols[7] ?? '').trim(),
      notes:           (cols[8] ?? '').trim(),
    }
    return validateRow(raw, idx + 1)
  })

  return {
    rows,
    validCount:   rows.filter(r => r.valid).length,
    invalidCount: rows.filter(r => !r.valid).length,
    totalCount:   rows.length,
  }
}

export const generateCSVTemplate = (): string => {
  const headers = 'contact_name,contact_phone,dropoff_address,size,weight,fragile,payment_method,price,notes'
  const example1 = 'Ali Ben Ahmed,55123456,25 Av Habib Bourguiba Tunis,medium,2.5,false,cash,45,Fragile SVP'
  const example2 = 'Sonia Trabelsi,22334455,Rue de la Liberté Sfax,small,,false,cash,30,'
  const example3 = 'Mohamed Slim,98765432,Boulevard 7 Novembre Sousse,large,8,true,online,120,Appeler avant'
  return [headers, example1, example2, example3].join('\n')
}
